import type { FieldValues, UseFormTrigger } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useBookingsActions } from "@ryde/redux";
import {
  createErrorResult,
  errors,
  Log,
  unique,
  type IBulkSetBookingsDto,
  type IPassengerGetStationsDto,
  type SimpleResult,
  type TDrawerCloseFunction,
} from "@ryde/shared";

import { useBookingsPollingHandlingActions } from "../../../../../services";
import type { IUseBulkBookingGetPayloadParams } from "../bulk-bookings.models";
import { useBulkBookingFlow } from "./use-bulk-booking-flow";

export type IUseBulkCreateGetPayloadParams = IUseBulkBookingGetPayloadParams;

interface IUseBulkCreateBookingArguments<T extends FieldValues> {
  onCloseDrawer: TDrawerCloseFunction;
  getPayload: (params: IUseBulkCreateGetPayloadParams) => IBulkSetBookingsDto;
  isDirty: boolean;
  trigger: UseFormTrigger<T>;
}

export const useBulkCreateBooking = <T extends FieldValues>({
  onCloseDrawer,
  getPayload,
  isDirty,
  trigger,
}: IUseBulkCreateBookingArguments<T>) => {
  const { t } = useTranslation();
  const { searchPassengerStations } = useBookingsActions();
  const { initBookingPollingHandler } = useBookingsPollingHandlingActions();

  const bulkBookingFlow = useBulkBookingFlow<T, IBulkSetBookingsDto>({
    onCloseDrawer,
    getPayload,
    startPolling: initBookingPollingHandler,
    isDirty,
    trigger,
  });
  const { approvalBookingConflicts } = bulkBookingFlow;

  const fetchPassengerStationsAndSetValue = useEvent(
    async (): Promise<SimpleResult<IPassengerGetStationsDto[]>> => {
      const payload: IBulkSetBookingsDto = getPayload({
        isPreview: true,
        approvalConflicts: approvalBookingConflicts,
      });

      const passengerIds = unique(
        payload.passengersData.map(({ passengerId }) => passengerId),
      );
      const passenger = payload.passengersData[0];

      if (unique(passengerIds).length > 1 || !passenger) {
        return createErrorResult(errors.booking.missingPassenger);
      }

      const passengerStationsResult = await searchPassengerStations({
        passengerId: passenger.passengerId,
        direction: passenger.direction,
        shiftId: passenger.shiftId,
      });

      if (!passengerStationsResult.success || !passengerStationsResult.data) {
        Log.warnAndToast(
          t(errors.booking.failedToFetchPassengerStations),
          passengerStationsResult.error,
        );
        return passengerStationsResult;
      }

      return passengerStationsResult;
    },
  );

  return {
    ...bulkBookingFlow,
    fetchPassengerStationsAndSetValue,
  };
};
